import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  InputBase,
  TextField,
  Typography,
} from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import { countryCodes } from '../../data/countryCodes';
import { resolvePhoneInputValue } from '../../utils/phoneUtils';
import { FIELD_PLACEHOLDER_COLOR, fieldLabelSx } from './formFieldStyles';
import { BRAND } from '../../theme/brandTokens';

const findCountryByCode = (code) => countryCodes.find((country) => country.code === code) || null;

const SalovarePhoneInput = ({
  label,
  countryCode = '+351',
  number = '',
  onCountryCodeChange,
  onNumberChange,
  error = false,
  helperText,
  placeholder = '',
  disabled = false,
  required = false,
  /** Distinguishes multiple phone fields on the same form for browser autofill (e.g. owner / store). */
  autoCompleteSection,
}) => {
  const [focused, setFocused] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [countryInputReadOnly, setCountryInputReadOnly] = useState(true);
  const [countryFieldKey, setCountryFieldKey] = useState(0);
  const selectedCountry = findCountryByCode(countryCode);
  const selectedCodeLabel = selectedCountry?.code || countryCode;
  const [countryInputValue, setCountryInputValue] = useState(selectedCodeLabel);
  const countryInputRef = useRef(null);
  const numberInputRef = useRef(null);

  const numberAutoComplete = autoCompleteSection
    ? `section-${autoCompleteSection} tel-national`
    : 'tel-national';
  const numberFieldName = autoCompleteSection
    ? `phone-${autoCompleteSection}`
    : 'phone';
  const countryFieldName = autoCompleteSection
    ? `phone-country-${autoCompleteSection}`
    : 'salovare-phone-country-code';

  useEffect(() => {
    if (!countryOpen) {
      setCountryInputValue(selectedCodeLabel);
    }
  }, [selectedCodeLabel, countryOpen]);

  const borderColor = error
    ? '#f44336'
    : focused
      ? BRAND.accent
      : BRAND.line;

  const applyResolvedPhone = (rawValue) => {
    const resolved = resolvePhoneInputValue(rawValue, countryCode);
    if (!resolved) {
      return null;
    }

    if (resolved.countryCode !== countryCode) {
      onCountryCodeChange?.(resolved.countryCode);
    }
    onNumberChange?.(resolved.number);
    return resolved;
  };

  const recoverFromCountryAutofill = (rawValue) => {
    const resolved = applyResolvedPhone(rawValue);
    if (!resolved) {
      return false;
    }

    const nextLabel = findCountryByCode(resolved.countryCode)?.code || resolved.countryCode;
    setCountryInputValue(nextLabel);
    setCountryOpen(false);
    setCountryInputReadOnly(true);
    // Remount so Chrome cannot leave leftover autofill text in the Autocomplete DOM.
    setCountryFieldKey((key) => key + 1);
    return true;
  };

  useEffect(() => {
    const input = countryInputRef.current;
    if (!input) {
      return undefined;
    }

    const handleAnimationStart = (event) => {
      if (event.animationName !== 'salovarePhoneCountryAutofill') {
        return;
      }
      recoverFromCountryAutofill(input.value);
    };

    input.addEventListener('animationstart', handleAnimationStart);
    return () => input.removeEventListener('animationstart', handleAnimationStart);
    // Remounted via countryFieldKey; recoverFromCountryAutofill closes over latest countryCode callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryFieldKey, countryCode]);

  const applyNumberValue = (rawValue) => {
    if (applyResolvedPhone(rawValue)) {
      return;
    }
    onNumberChange?.(rawValue);
  };

  useEffect(() => {
    const input = numberInputRef.current;
    if (!input) {
      return undefined;
    }

    const handleValue = () => applyNumberValue(input.value);
    const handleAnimationStart = (event) => {
      if (event.animationName !== 'salovarePhoneNumberAutofill') {
        return;
      }
      handleValue();
    };

    if (input.value) {
      handleValue();
    }

    input.addEventListener('input', handleValue);
    input.addEventListener('animationstart', handleAnimationStart);
    return () => {
      input.removeEventListener('input', handleValue);
      input.removeEventListener('animationstart', handleAnimationStart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  return (
    <Box>
      {label ? (
        <Typography variant="subtitle2" sx={fieldLabelSx}>
          {label}{required ? ' *' : ''}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          height: 48,
          border: `2px solid ${borderColor}`,
          borderRadius: '8px',
          backgroundColor: disabled ? BRAND.background : '#fff',
          transition: 'border-color 0.3s ease',
          overflow: 'hidden',
          opacity: disabled ? 0.7 : 1,
          '@keyframes salovarePhoneCountryAutofill': {
            from: { opacity: 1 },
            to: { opacity: 1 },
          },
          '@keyframes salovarePhoneNumberAutofill': {
            from: { opacity: 1 },
            to: { opacity: 1 },
          },
          '&:hover': {
            borderColor: disabled || error ? borderColor : BRAND.accent,
          },
        }}
      >
        <Autocomplete
          key={countryFieldKey}
          open={countryOpen}
          onOpen={() => setCountryOpen(true)}
          onClose={() => {
            setCountryOpen(false);
            setCountryInputValue(selectedCodeLabel);
          }}
          value={selectedCountry}
          inputValue={countryInputValue}
          onChange={(_, newValue) => {
            if (newValue) {
              onCountryCodeChange?.(newValue.code);
              setCountryInputValue(newValue.code);
            }
          }}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset') {
              setCountryInputValue(selectedCodeLabel);
              return;
            }
            if (reason !== 'input') {
              return;
            }
            if (recoverFromCountryAutofill(value)) {
              return;
            }
            setCountryInputValue(value);
          }}
          options={countryCodes}
          getOptionLabel={(option) => option.code}
          isOptionEqualToValue={(option, value) => (
            option.code === value?.code && option.country === value?.country
          )}
          disableClearable
          disabled={disabled}
          filterOptions={(options, state) => {
            const search = state.inputValue.trim().toLowerCase();
            if (!search) return options;
            if (resolvePhoneInputValue(state.inputValue, countryCode)) {
              return options;
            }
            return options.filter((option) => {
              const searchableText = `${option.name} ${option.country} ${option.code}`.toLowerCase();
              return searchableText.includes(search);
            });
          }}
          sx={{
            width: 132,
            flexShrink: 0,
            '& .MuiOutlinedInput-root': {
              height: 44,
              pl: 1,
              pr: '28px !important',
              bgcolor: 'transparent',
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' },
            },
            '& .MuiAutocomplete-input': {
              fontSize: 15,
              fontWeight: 600,
              p: '0 !important',
              minWidth: '0 !important',
            },
            '& .MuiAutocomplete-endAdornment': {
              right: 2,
            },
            '& input:-webkit-autofill': {
              animationName: 'salovarePhoneCountryAutofill',
              animationDuration: '0.001s',
            },
          }}
          slotProps={{
            popper: {
              disableScrollLock: true,
              sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              onFocus={(event) => {
                setFocused(true);
                setCountryInputReadOnly(false);
                params.inputProps?.onFocus?.(event);
              }}
              onBlur={(event) => {
                setFocused(false);
                setCountryInputReadOnly(true);
                setCountryInputValue(selectedCodeLabel);
                params.inputProps?.onBlur?.(event);
              }}
              inputProps={{
                ...params.inputProps,
                ref: (node) => {
                  countryInputRef.current = node;
                  const { ref } = params.inputProps;
                  if (typeof ref === 'function') {
                    ref(node);
                  } else if (ref) {
                    ref.current = node;
                  }
                },
                autoComplete: 'off',
                autoCorrect: 'off',
                spellCheck: false,
                name: countryFieldName,
                readOnly: countryInputReadOnly,
                onChange: (event) => {
                  params.inputProps?.onChange?.(event);
                  recoverFromCountryAutofill(event.target.value);
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedCountry ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.75 }}>
                    <ReactCountryFlag
                      countryCode={selectedCountry.country}
                      svg
                      aria-label={selectedCountry.name}
                      style={{
                        width: '1.15rem',
                        height: '1.15rem',
                        borderRadius: '2px',
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                ) : params.InputProps.startAdornment,
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box component="li" key={`${option.country}-${option.code}-${key}`} {...optionProps}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, width: '100%' }}>
                  <ReactCountryFlag
                    countryCode={option.country}
                    svg
                    aria-label={option.name}
                    style={{
                      width: '1.25rem',
                      height: '1.25rem',
                      borderRadius: '2px',
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {option.code}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ ml: 'auto' }}>
                    {option.name}
                  </Typography>
                </Box>
              </Box>
            );
          }}
        />

        <Box
          sx={{
            width: '1px',
            alignSelf: 'stretch',
            my: 1,
            bgcolor: focused && !error ? BRAND.accent : BRAND.line,
            transition: 'background-color 0.3s ease',
          }}
        />

        <InputBase
          type="tel"
          name={numberFieldName}
          autoComplete={numberAutoComplete}
          inputMode="tel"
          inputRef={numberInputRef}
          value={number}
          onChange={(event) => applyNumberValue(event.target.value)}
          onInput={(event) => applyNumberValue(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          sx={{
            flex: 1,
            px: 1.5,
            fontSize: 16,
            '& input::placeholder': {
              color: FIELD_PLACEHOLDER_COLOR,
              opacity: 1,
            },
            '& input:-webkit-autofill': {
              animationName: 'salovarePhoneNumberAutofill',
              animationDuration: '0.001s',
            },
          }}
        />
      </Box>

      {helperText ? (
        <Typography
          variant="caption"
          sx={{
            color: error ? '#f44336' : 'text.secondary',
            mt: 0.5,
            display: 'block',
          }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
};

export default SalovarePhoneInput;
